<template>
  <aside class="account-menu">
    <div class="account-menu-label">Menu</div>

    <nav class="account-menu-list" aria-label="Account navigation">
      <div
        v-for="group in groupedAdminOptions"
        :key="group.name"
        class="account-menu-group"
      >
        <div class="account-menu-group-label">{{ group.name }}</div>
        <router-link
          v-for="option in group.items"
          :key="option.name"
          :to="option.url"
          class="account-menu-link"
          :class="{ 'is-active': isActive(option) }"
        >
          <span class="icon" :class="selectClass(option.name)"></span>
          <span>{{ option.name }}</span>
        </router-link>
      </div>

      <button type="button" class="account-menu-link account-menu-button" @click="logoutLocal">
        <span class="icon icon-log-out"></span>
        <span>Logout</span>
      </button>
    </nav>
  </aside>
</template>

<script>
import { mapGetters, mapActions} from "vuex";

export default {
  name: "Menu",
  computed: {
    ...mapGetters(["allAdminOptions"]),
    groupedAdminOptions() {
      const options = Object.keys(this.allAdminOptions || {}).map((key) => this.allAdminOptions[key])
      const order = ["Account", "Products", "Automation", "AI", "Administration"]
      const groups = []

      order.forEach((section) => {
        const items = options.filter((option) => (option.section || "Account") === section)
        if (items.length) groups.push({ name: section, items })
      })

      const grouped = groups.reduce((acc, group) => {
        group.items.forEach((option) => { acc[option.name] = true })
        return acc
      }, {})
      const uncategorized = options.filter((option) => !grouped[option.name])
      if (uncategorized.length) groups.push({ name: "Other", items: uncategorized })

      return groups
    },
  },
  methods: {
    ...mapActions([
      'logout'
    ]),
    async logoutLocal(){
      await this.logout()
      this.$router.push('/').catch(()=>{})
    },
    isActive(option) {
      return this.$route.path == option.url || this.$route.path.indexOf(option.url + '/') == 0
    },
    selectClass(name) {
      switch (name) {
        case "Dashboard":
          return "icon-gauge";
        case "Admin Overview":
          return "icon-bar-graph";
        case "Profile":
          return "icon-user";
        case "Settings":
          return "icon-cog";
        case "All Parts":
          return "icon-add-to-list";
        case "Billing":
          return "icon-credit-card";
        case "My Builds":
          return "icon-shopping-cart";
        case "My Orders":
          return "icon-text-document";
        case "Scheduled Jobs":
          return "icon-cycle";
        case "Pipelines":
          return "icon-cycle";
        case "Onboard New Parts":
          return "icon-list";
        case "Catalog Health":
          return "icon-gauge";
        case "Build Coverage":
          return "icon-grid";
        case "User Management":
          return "icon-users";
        case "Audience":
          return "icon-bar-graph";
        case "AI Usage":
          return "icon-credit-card";
        case "AI Prompts":
          return "icon-text-document";
        case "SEO Advisor":
          return "icon-gauge";
        case "Subscribers":
          return "icon-mail";
        default:
          return "icon-dot-single";
      }
    },
  },
};
</script>

<style scoped>
.account-menu {
	color: #cfd2da;
}

.account-menu-label {
	margin-bottom: 0.8rem;
	padding-bottom: 0.85rem;
	border-bottom: 1px solid rgba(67, 72, 87, 0.86);
	color: #cfd2da;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0.8px;
	line-height: 1;
	text-transform: uppercase;
}

.account-menu-list {
	display: grid;
	gap: 0.7rem;
}

.account-menu-group {
	display: grid;
	gap: 0.35rem;
}

.account-menu-group-label {
	padding: 0 0.65rem;
	color: #6f7b91;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 0.1em;
	line-height: 1;
	text-transform: uppercase;
}

.account-menu-link {
	position: relative;
	display: flex;
	align-items: center;
	gap: 0.65rem;
	width: 100%;
	min-height: 38px;
	padding: 0.55rem 0.65rem;
	border: 1px solid transparent;
	border-radius: 6px;
	background: transparent;
	color: #cfd2da;
	cursor: pointer;
	font-size: 15px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: 1;
	text-align: left;
	text-decoration: none;
	transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
}

.account-menu-link .icon {
	display: inline-flex;
	justify-content: center;
	width: 18px;
	color: #9198aa;
	font-size: 15px;
	transition: color 160ms ease;
}

.account-menu-link:hover,
.account-menu-link:focus,
.account-menu-link.is-active {
	border-color: rgba(25, 151, 198, 0.56);
	background: rgba(25, 151, 198, 0.08);
	color: #f4f8fb;
	text-decoration: none;
}

.account-menu-link:hover .icon,
.account-menu-link:focus .icon,
.account-menu-link.is-active .icon {
	color: #1997c6;
}

.account-menu-link.is-active::before {
	position: absolute;
	top: 8px;
	bottom: 8px;
	left: -1px;
	width: 2px;
	border-radius: 2px;
	background: #1997c6;
	box-shadow: 0 0 12px rgba(25, 151, 198, 0.8);
	content: "";
}

.account-menu-button {
	font-family: inherit;
}

@media screen and (max-width: 1200px) {
	.account-menu {
		width: 100%;
	}

	.account-menu-label {
		margin-bottom: 0.55rem;
		padding-bottom: 0;
		border-bottom: 0;
		color: #7f8aa0;
		font-size: 11px;
		font-weight: 800;
		letter-spacing: 0.12em;
	}

	.account-menu-list {
		display: flex;
		align-items: stretch;
		gap: 0.65rem;
		width: 100%;
		padding-bottom: 0.25rem;
		overflow-x: auto;
		overflow-y: hidden;
		scrollbar-width: thin;
		scrollbar-color: rgba(25, 151, 198, 0.55) rgba(8, 10, 15, 0.35);
	}

	.account-menu-list::-webkit-scrollbar {
		display: block !important;
		height: 7px;
	}

	.account-menu-list::-webkit-scrollbar-track {
		background: rgba(8, 10, 15, 0.35);
	}

	.account-menu-list::-webkit-scrollbar-thumb {
		border-radius: 999px;
		background: rgba(25, 151, 198, 0.55);
	}

	.account-menu-group {
		display: flex;
		align-items: stretch;
		flex: 0 0 auto;
		gap: 0.35rem;
		padding-right: 0.65rem;
		border-right: 1px solid rgba(67, 72, 87, 0.58);
	}

	.account-menu-group-label {
		display: flex;
		align-items: center;
		flex: 0 0 auto;
		padding: 0 0.2rem 0 0;
		color: #69758b;
		font-size: 10px;
		white-space: nowrap;
	}

	.account-menu-link {
		flex: 0 0 auto;
		width: auto;
		min-height: 36px;
		padding: 0.48rem 0.62rem;
		border-color: rgba(67, 72, 87, 0.58);
		background: rgba(8, 10, 15, 0.32);
		font-size: 13px;
		white-space: nowrap;
	}

	.account-menu-link.is-active::before {
		top: auto;
		right: 8px;
		bottom: -1px;
		left: 8px;
		width: auto;
		height: 2px;
	}
}

@media screen and (max-width: 640px) {
	.account-menu-group-label {
		display: none;
	}

	.account-menu-link {
		min-height: 34px;
		font-size: 12px;
	}
}
</style>
